import FrontSonda from '@gcloud-npm/utils.monitoring.front-sonda';
import { applyTransform2Object } from "../../utils/common";
import { getUUID } from "../../utils/monitoring";
import {
  Context,
  MethodType,
  UnknownFunctionWithUnknownReturn,
  UnknownObject,
} from "../../utils/types";
import Request, { getMockedInfo } from "../../utils/request";
import Sonda from "../../utils/sonda";

export const processName = "ADABYRON-API";
export const processNameTges = `${processName}-TGES`;
export const processNameOpen = `${processName}-OPEN`;

export enum USER_TYPES {
  CLIENT = "Telco",
  TGES = "Gestor",
  OPEN = "",
}

type Params = string | number | boolean | Array<String>;
export type QueryParams = Array<{
  param: string;
  value?: Params | undefined | null;
}>;
export type URLParamsToReplace = Array<{
  param: string;
  value: Params;
}>;

export const buildApiUrl = (
  endpoint: string,
  idRequest: string,
  queryParams?: QueryParams,
  urlParamsToReplace?: URLParamsToReplace
): string => {
  let URL_API = `${endpoint}?${idRequest}`;

  if (queryParams) {
    for (const queryParam of queryParams) {
      const { param, value } = queryParam;

      if (param) {
        if (!Object.prototype.hasOwnProperty.call(queryParam, "value")) {
          URL_API += `&${param}`;
        } else if (value !== undefined) {
          URL_API += `&${param}=${String(value)}`;
        }
      }
    }
  }

  if (urlParamsToReplace) {
    urlParamsToReplace.forEach((paramToReplace) => {
      const { param, value } = paramToReplace;
      URL_API = URL_API.replace(param, String(value));
    });
  }

  return URL_API;
};

export interface GeneralAdabyronBehaviourParams {
  context: Context;
  idFront: string;
  path: string;
  userTypes: USER_TYPES[];
  eventName: string;
  queryParams?: QueryParams;
  urlParamsToReplace?: URLParamsToReplace;
  mockedInfo: string;
  methodType: MethodType;
  requestObject?: Record<string, ObjectToTransformValue>;
  onErrorCallback?: UnknownFunctionWithUnknownReturn;
  onSuccessCallback?: UnknownFunctionWithUnknownReturn;
  maximizeRetrievedData?: boolean;
  onSuccessNormalizer?: (datos: unknown) => unknown;
  requestHeaders?: Record<string, string>;
  telcoHostVersion?: number;
}

/**
 * generalAdabyronBehaviour: Comportamiento predefinido para una promesa de Adabyron que se puede personalizar.
 * @param context Contexto necesario
 * @param idFront IDFront necesario
 * @param path  Path necesario
 * @param {array} userTypes Tipo de usuario que puede acceder al método
 * @param {string} eventName Nombre del evento
 * @param {array} queryParams (optional): Parámetros a añadir a la URL
 * @param {array} urlParamsToReplace (optional): Parámetros a reemplazar en la URL
 * @param {string} mockedInfo Mocked info
 * @param {MethodType} methodType Tipo de método en la request: GET, POST, PUT, HEAD
 * @param {object} requestObject (optional): Datos necesarios para la request => (PUT / POST)
 * @param {function} onErrorCallback (optional): Callback que se ejecuta cuando la petición ha sido rechazada.
 * @param {function} onSuccessCallback (optional): Callback que se ejecuta cuando la petición ha ido correctamente.
 * @param {function} maximizeRetrievedData (optional): Flag que incluye información adicional disponible en la respuesta.
 * @param {function} onSuccessNormalizer (optional): Función normalizadora para los datos cuando la petición ha ido correctamente.
 * @param {object} requestHeaders (optional): Headers extra necesarios
 * @returns {promise} Promesa de la request
 */

export const generalAdabyronBehaviour = (
  params: GeneralAdabyronBehaviourParams
): Promise<unknown> => {
  const {
    context,
    idFront,
    path,
    userTypes,
    eventName,
    queryParams,
    urlParamsToReplace,
    mockedInfo,
    methodType,
    requestObject,
    onErrorCallback,
    onSuccessCallback,
    maximizeRetrievedData,
    onSuccessNormalizer,
    requestHeaders,
    telcoHostVersion,
  } = params;

  const idRequest = getUUID(idFront);
  const { name } = context.environment;

  const { endpoint, headers, processName, allowed } = getTelcoHost(
    context,
    path,
    userTypes,
    telcoHostVersion
  );

  if (!allowed) {
    return getMethodNotAllowedReject(path);
  }

  const sonda = new Sonda({
    eventName: `${eventName}${telcoHostVersion ? "_V" + telcoHostVersion : ""}`,
    context,
    uniqueId: idRequest,
    origin: processName,
  });

  const URL_API = buildApiUrl(
    endpoint,
    idRequest,
    queryParams,
    urlParamsToReplace
  );

  const mockInfo = getMockedInfo(mockedInfo, name);

  const onSuccessDefault = (xhr: XMLHttpRequest) => {
    const { response } = xhr;
    const { datos } = JSON.parse(response);
    return onSuccessNormalizer ? onSuccessNormalizer(datos) : datos;
  };

  const onSuccessMaximizeData = (xhr: XMLHttpRequest) => {
    const { response } = xhr;
    const { datos, warnings } = JSON.parse(response);
    const processedResponse = { ...datos, warnings };
    return onSuccessNormalizer
      ? onSuccessNormalizer(processedResponse)
      : processedResponse;
  };

  try {
    const request = new Request({
      method: methodType,
      data:
        (methodType === MethodType.POST || methodType === MethodType.PUT) &&
        requestObject &&
        requestObject,
      endpoint: URL_API,
      onError:
        onErrorCallback ||
        ((response: XMLHttpRequest) => normalizeObjError(response)),
      onSuccess:
        onSuccessCallback ||
        (maximizeRetrievedData
          ? (response: XMLHttpRequest) => onSuccessMaximizeData(response)
          : (response: XMLHttpRequest) => onSuccessDefault(response)),
      sonda,
      headers: requestHeaders ? { ...headers, ...requestHeaders } : headers,
      mockInfo,
    });

    return request.makeRequest();
  } catch (err) {
    sonda.errorJs(err);
    return null;
  }
};

export const normalizeRedirectError = (): string => {
  const { host, protocol, href } = window.location;
  const protocolTrimmed = protocol.split(":")[0];
  const loginUrlComposed = `${protocolTrimmed}://${host}/particulares/cms/auth/expired/?origin=web&reason=TCOL_unauthorized&redirect=${encodeURIComponent(
    href
  )}`;
  return loginUrlComposed;
};

export const makeLoginRedirect = (): void => {
  window.location.href = normalizeRedirectError();
};

type ObjectToTransformValue =
  | string
  | number
  | boolean
  | Record<string, unknown>
  | Array<unknown>
  | null;

export const makeUndefinedValueNull = (
  obj: Record<string, ObjectToTransformValue>
): Record<string, ObjectToTransformValue> => {
  return applyTransform2Object(
    [
      {
        type: "undefined",
        method: () => null,
      },
    ],
    (key) => key,
    obj
  ) as Record<string, ObjectToTransformValue>;
};

export const getMethodNotAllowedReject = (path: string): Promise<unknown> => {
  return Promise.reject({
    status: 418,
    statusText: "I'm a teapot",
    response: `Method not allowed for this type of user: ${path}`,
  });
};

export const getTelcoHost = (
  context: Context,
  path: string,
  userTypesAllowed: string[],
  version?: number
): {
  endpoint: string;
  headers: UnknownObject;
  processName: string;
  allowed: boolean;
} => {
  try {
    const { environment, userType } = context;

    if (userTypesAllowed.includes(userType)) {
      let { telco: connectionData } = environment;
      let prefixToApply = "/cliente/api/";
      let finalProcessName = processName;

      if (userType === USER_TYPES.TGES) {
        ({ tges: connectionData } = environment);
        prefixToApply = "";
        finalProcessName = processNameTges;
      }
      if (userType === USER_TYPES.OPEN) {
        prefixToApply = "/abierto/api/";
        finalProcessName = processNameOpen;
      }

      const { endpoint: host, headers } = connectionData;
      const pathBegin = path.substr(0, path.indexOf("/"));
      const pathEnd = path.substr(path.indexOf("/") + 1, path.length);
      let finalEndpoint = `${host}${prefixToApply}${pathBegin}/`;
      if (version) {
        finalEndpoint = `${finalEndpoint}v${version}/`;
      }

      return {
        endpoint: `${finalEndpoint}${pathEnd}`,
        headers,
        processName: finalProcessName,
        allowed: true,
      };
    } else {
      return {
        endpoint: "",
        headers: {},
        processName: "",
        allowed: false,
      };
    }
  } catch (error) {
    console.error(error);
    const frontSonda = new FrontSonda({
      processName: "getTelcoHost",
      context,
      filePath: "utils/ada-byron",
    } as any);
    frontSonda.errorMessage({ info: `error on path ${path}`, error });
  }
  return null;
};

export const normalizeObjError = (
  xhr: XMLHttpRequest
): Partial<XMLHttpRequest> => {
  let response: any;
  try {
    response = JSON.parse(xhr.response);
  } catch (error) {
    response = xhr.response;
  }
  const objError = {
    status: xhr.status,
    statusText: xhr.statusText,
    response,
  };
  if (
    objError.status === 401 &&
    objError.response &&
    objError.response.error &&
    objError.response.error.codigoErrorNegocio
  ) {
    const { codigoErrorNegocio } = objError.response.error;
    if (
      codigoErrorNegocio === "BACK-APIGW-001" ||
      codigoErrorNegocio === "BACK-SEGURIDAD-002" ||
      codigoErrorNegocio === "TCOL-ARQUITECTURA-020" ||
      codigoErrorNegocio === "TCOL-ARQUITECTURA-005"
    ) {
      makeLoginRedirect();
    }
  }

  return objError;
};
